import {
  Link,
  Button,
  Heading,
  Icon,
  Text,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Fade,
} from '@chakra-ui/react';
import { dataAttr } from '@chakra-ui/utils';
import { FC, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import EmailIcon from '@/assets/icons/ic-share-email.svg';
import FbIcon from '@/assets/icons/ic-share-fb.svg';
import LdIcon from '@/assets/icons/ic-share-ld.svg';
import TwIcon from '@/assets/icons/ic-share-x-circle.svg';

import { HighlightableText } from '@/components/ui/core/HighlightableText/HighlightableText';
import { NotificationText } from '@/components/ui/features/lecture/ShareLectureModal/ShareLectureModal.elements';
import { useIsClientSide } from '@/hooks/useIsClientSide';
import { useResource } from '@/lib/datx-jsonapi-react';
import { ThinLecture } from '@/resources/ThinLecture';

interface ICiteLectureModalProps {
  lectureId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ShareLectureModal: FC<ICiteLectureModalProps> = ({ lectureId, isOpen, onClose }) => {
  const { t } = useTranslation();
  const isClientSide = useIsClientSide();
  const elToCopy = useRef<HTMLParagraphElement>(null);
  const [isCopied, setCopied] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();

  const windowLocation = isClientSide && window.location.href;

  const { data: lecture } = useResource(() => (isOpen ? [ThinLecture, lectureId] : null));

  const onCopyLink = () => {
    copyToClipboard(elToCopy.current.innerHTML);

    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered variant="inverted">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Heading as="h4" fontSize="2rem" lineHeight="10" mb={8}>
            {t('newLecture.shareTitle')}
          </Heading>
          <Flex w="full" px={4} py={3} bg="gray.800" alignItems="center">
            <HighlightableText
              ref={elToCopy}
              data-highlighted={dataAttr(isCopied)}
              fontFamily="heading"
              color="white"
              py={2}
              noOfLines={1}
            >
              {windowLocation}
            </HighlightableText>
            <Button
              display="flex"
              flexShrink={0}
              ml={3}
              variant="unstyled"
              textTransform="uppercase"
              height="30px"
              onClick={() => onCopyLink()}
            >
              {t('newLecture.shareCopyButton')}
            </Button>
          </Flex>
          <Flex mt={4}>
            <Fade in={isCopied}>
              <NotificationText fontSize="sm">{t('newLecture.copySuccess')}</NotificationText>
            </Fade>
          </Flex>
          <Flex mt={4}>
            <Link
              mr={8}
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURI(windowLocation)}`}
              target="_blank"
              textAlign="center"
            >
              <Icon as={FbIcon} w="30px" h="30px" />
              <Text fontSize="xs" mt={2} fontWeight="normal">
                Facebook
              </Text>
            </Link>
            <Link
              target="_blank"
              textAlign="center"
              href={`https://twitter.com/intent/tweet?url=${windowLocation}&text=${lecture?.title}&via=`}
              mr={8}
            >
              <Icon as={TwIcon} w="30px" h="30px" />
              <Text fontSize="xs" mt={2} fontWeight="normal">
                X
              </Text>
            </Link>
            <Link
              target="_blank"
              textAlign="center"
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(windowLocation)}&title=${
                lecture?.title
              }/?latest`}
              mr={8}
            >
              <Icon as={LdIcon} w="30px" h="30px" />
              <Text fontSize="xs" mt={2} fontWeight="normal">
                Linkedin
              </Text>
            </Link>
            <Link
              textAlign="center"
              href={`mailto:?body=${encodeURI(windowLocation)}&subject=${encodeURIComponent(lecture?.title)}`}
              mr={8}
            >
              <Icon as={EmailIcon} w="30px" h="30px" />
              <Text fontSize="xs" mt={2} fontWeight="normal">
                Email
              </Text>
            </Link>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
